"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";

interface AccordionContextValue {
  value: string | null;
  onValueChange: (value: string | null) => void;
  type: "single" | "multiple";
  openItems: Set<string>;
}

const AccordionContext = React.createContext<AccordionContextValue>({
  value: null,
  onValueChange: () => {},
  type: "single",
  openItems: new Set(),
});

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: "single" | "multiple";
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, type = "single", defaultValue, value, onValueChange, children, ...props }, ref) => {
    const [openItems, setOpenItems] = React.useState<Set<string>>(
      new Set(defaultValue ? [defaultValue] : [])
    );

    const handleValueChange = React.useCallback(
      (itemValue: string | null) => {
        if (!itemValue) return;
        setOpenItems((prev) => {
          const next = new Set(prev);
          if (next.has(itemValue)) {
            next.delete(itemValue);
          } else {
            if (type === "single") next.clear();
            next.add(itemValue);
          }
          return next;
        });
        onValueChange?.(itemValue);
      },
      [type, onValueChange]
    );

    const controlled = value !== undefined;
    const effectiveOpenItems = controlled ? new Set([value as string]) : openItems;

    return (
      <AccordionContext.Provider
        value={{
          value: value ?? null,
          onValueChange: handleValueChange,
          type,
          openItems: effectiveOpenItems,
        }}
      >
        <div ref={ref} className={cn("", className)} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  }
);
Accordion.displayName = "Accordion";

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, value, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("border-b border-border", className)}
      data-value={value}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<{ itemValue?: string }>, {
            itemValue: value,
          });
        }
        return child;
      })}
    </div>
  )
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  itemValue?: string;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ className, children, itemValue, ...props }, ref) => {
    const { openItems, onValueChange } = React.useContext(AccordionContext);
    const isOpen = itemValue ? openItems.has(itemValue) : false;

    return (
      <div className="flex">
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          className={cn(
            "flex h-auto flex-1 items-center justify-between rounded-none bg-transparent px-0 py-4 text-sm font-medium transition-all hover:bg-transparent hover:underline [&[data-state=open]>svg]:rotate-180",
            className
          )}
          data-state={isOpen ? "open" : "closed"}
          onClick={() => onValueChange(itemValue ?? null)}
          aria-expanded={isOpen}
          {...props}
        >
          {children}
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
        </Button>
      </div>
    );
  }
);
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  itemValue?: string;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ className, children, itemValue, ...props }, ref) => {
    const { openItems } = React.useContext(AccordionContext);
    const isOpen = itemValue ? openItems.has(itemValue) : false;

    if (!isOpen) return null;

    return (
      <div
        ref={ref}
        className={cn("overflow-hidden text-sm", className)}
        data-state={isOpen ? "open" : "closed"}
        {...props}
      >
        <div className="pb-4 pt-0">{children}</div>
      </div>
    );
  }
);
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
